import { LitElement, html, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { StoreMixin, store } from '../store.js';
import { api } from '../api.js';
import { apiParams } from '../lib/filters.js';
import {
  metricRows,
  metricTiles,
  snapshotDrift,
  taxonomyRows,
  qualityMetricColumns,
  qualityTaxonomyColumns,
} from '../lib/hygiene.js';
import { withFormats } from '../lib/table.js';
import { hours as formatHours, count, percent, longDate, humanize } from '../lib/format.js';

/** The shared filters this view reads. The metrics are a property of one run and nothing else. */
const METRICS_PARAM_KEYS = ['run_id'];

/**
 * The quality metrics of one run (PRD 12.2; TRD 11; Phase 34), beneath the worklist that acts on
 * them. Six percentages and two counts, each drawn with **the numerator and the denominator it
 * came from**, because a percentage nobody can re-derive is a number nobody can argue with — and
 * these are the numbers that decide whether the forecast above them is worth planning against.
 *
 * Every figure is a field of `GET /quality/metrics` for the run in the filter bar. The tile shows
 * the fraction the API returned; the line beneath it shows the two integers, which are countable
 * by hand from the same run's project and allocation lists. That is the whole design: the card is
 * a rendering of one row of `data_quality_metrics` and holds no arithmetic of its own.
 *
 * **Two tables, two lifetimes** (see the quality module's README). The metrics row is immutable
 * with the rest of the run; the worklist above outlives every run, and somebody may have
 * dismissed an item since this one was computed. When the two disagree the card says so rather
 * than letting a reader find it and conclude one of them is broken.
 *
 * A percentage whose denominator is zero is null in the API and stays null here — "no projects in
 * the denominator" is a different statement from 0%, and only one of them is true.
 */
class QualityMetrics extends StoreMixin(LitElement) {
  static properties = {
    /** The worklist's response, handed down by the page, for the snapshot comparison. */
    worklist: { type: Object },
    _data: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _tableOpen: { state: true },
    _taxonomyOpen: { state: true },
  };

  constructor() {
    super();
    this.worklist = null;
    this._data = null;
    this._loading = false;
    this._error = null;
    this._tableOpen = false;
    this._taxonomyOpen = false;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
      }
      .card {
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 24px 18px;
      }
      .head {
        margin-bottom: 16px;
      }
      .eyebrow {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      h2 {
        margin: 6px 0 4px;
        font-size: 20px;
        line-height: 1.2;
        color: var(--ink-strong);
        max-width: 44ch;
      }
      .sub {
        color: var(--ink-muted);
        font-size: 13px;
        max-width: 82ch;
      }

      .tiles {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(216px, 1fr));
        gap: 12px;
      }
      .tile {
        border: 1px solid var(--border-soft);
        border-radius: 8px;
        background: var(--surface-2);
        padding: 12px 14px 13px;
      }
      .tile .label {
        font-size: 12px;
        color: var(--ink-muted);
        line-height: 1.3;
        min-height: 2.6em;
      }
      .tile .value {
        font-size: 26px;
        font-weight: 700;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
        line-height: 1.1;
        margin-top: 2px;
      }
      .tile .value.none {
        font-size: 15px;
        font-weight: 600;
        color: var(--ink-muted);
      }
      /* The meter is the second channel, never the only one: the fraction is always in figures
       above it and the two integers behind it are always beneath. */
      .meter {
        height: 5px;
        border-radius: 3px;
        background: var(--surface-sunken);
        overflow: hidden;
        margin: 8px 0 6px;
      }
      .meter span {
        display: block;
        height: 100%;
        background: var(--ink-strong);
      }
      .tile .derive {
        font-size: 12px;
        color: var(--ink);
        font-variant-numeric: tabular-nums;
      }
      .tile .note {
        font-size: 11.5px;
        color: var(--ink-muted);
        margin-top: 4px;
        line-height: 1.35;
      }

      .notes {
        margin: 14px 0 0;
        font-size: 12.5px;
        color: var(--ink-muted);
        max-width: 88ch;
      }
      .notes p {
        margin: 0 0 6px;
      }
      .drift {
        border-left: 3px solid var(--status-caution);
        background: var(--status-caution-tint);
        color: var(--ink);
        padding: 8px 12px;
        border-radius: 0 6px 6px 0;
        margin: 14px 0 0;
        font-size: 12.5px;
        max-width: 88ch;
      }

      .state {
        padding: 24px 12px;
        text-align: center;
        color: var(--ink-muted);
        font-size: 13.5px;
      }
      .state.error {
        color: var(--status-overloaded);
      }

      details.table {
        margin-top: 16px;
        border-top: 1px solid var(--border-soft);
        padding-top: 12px;
      }
      details.table summary {
        cursor: pointer;
        font-size: 13px;
        font-weight: 600;
        color: var(--ink-strong);
      }
      details.table data-table {
        margin-top: 12px;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    this._unsubscribe = store.subscribe(() => this._maybeFetch());
    this._maybeFetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribe?.();
  }

  _params() {
    const { filters, run } = this.appState;
    if (!run) return null;
    return apiParams(filters, METRICS_PARAM_KEYS);
  }

  async _maybeFetch() {
    const params = this._params();
    const key = params ? JSON.stringify(params) : null;
    if (key === this._requestKey) return;
    this._requestKey = key;
    if (!params) {
      this._data = null;
      return;
    }
    this._loading = true;
    this._error = null;
    try {
      const data = await api.get('/quality/metrics', params);
      if (this._requestKey !== key) return;
      this._data = data;
    } catch (err) {
      if (this._requestKey !== key) return;
      // A run from before Phase 16 stored no metrics, and a failed or running one has none to
      // read. Both are facts about the run the reader chose, not failures of this card.
      this._error =
        err.code === 'NOT_FOUND'
          ? 'This run stored no quality metrics — it predates the metrics themselves. Pick a later run in the filter bar.'
          : err.code === 'RUN_NOT_READABLE'
            ? 'That run has not succeeded, so it has no metrics to read.'
            : `Could not load the quality metrics: ${err.message}`;
      this._data = null;
    } finally {
      if (this._requestKey === key) this._loading = false;
    }
  }

  _tile(t) {
    const missing = t.value === null || t.value === undefined;
    const meter = t.kind === 'percent' && !missing;
    return html`
      <div class="tile" data-metric=${t.key}>
        <div class="label">${t.label}</div>
        ${
          missing
            ? html`<div class="value none">Nothing in the denominator</div>`
            : html`<div class="value">
                ${t.kind === 'percent' ? percent(t.value) : count(t.value)}
              </div>`
        }
        ${
          meter
            ? html`<div class="meter" aria-hidden="true">
                <span style=${`width: ${Math.min(100, Math.max(0, t.value * 100))}%`}></span>
              </div>`
            : nothing
        }
        ${
          t.kind === 'percent'
            ? html`<div class="derive">
                ${
                  t.of === 'hours'
                    ? html`${formatHours(t.numerator)} of ${formatHours(t.denominator)}`
                    : html`${count(t.numerator)} of ${count(t.denominator)} ${t.of}`
                }
              </div>`
            : html`<div class="derive">${t.of}</div>`
        }
        <div class="note">${t.note}</div>
      </div>
    `;
  }

  /** What the run's own worklist snapshot said, against what the worklist says now. */
  _drift() {
    const drift = snapshotDrift(this._data, this.worklist);
    if (!drift || !drift.changed) return nothing;
    return html`
      <p class="drift" data-drift>
        This run counted <strong>${count(drift.open_then)}</strong> open items worth
        ${formatHours(drift.hours_then)}; the worklist above has
        <strong>${count(drift.open_now)}</strong> open now. The metrics row is frozen with the run;
        the worklist outlives every run, so somebody has resolved, dismissed or reopened something
        since. Both numbers are right.
      </p>
    `;
  }

  get _metricColumns() {
    // The specification is shared with the server's CSV (lib/hygiene.js).
    return withFormats(qualityMetricColumns(), {
      byKey: {
        value: (v, row) => (v === null ? '—' : row.kind === 'percent' ? percent(v) : count(v)),
      },
    });
  }

  get _taxonomyColumns() {
    return withFormats(qualityTaxonomyColumns(), {
      byKey: { source_system: (v) => humanize(v) },
    });
  }

  _subtitle() {
    const d = this._data;
    if (!d) return 'The completeness and inference ratios of one forecast run.';
    return `Run of ${longDate(d.planning_date)}, opportunities ${d.pipeline_mode}. Every fraction is shown with the two numbers behind it, so each one can be counted by hand from the same run.`;
  }

  render() {
    const d = this._data;
    const { run } = this.appState;

    let body;
    if (!run) body = html`<div class="state">No forecast run yet — nothing to measure.</div>`;
    else if (this._error) body = html`<div class="state error">${this._error}</div>`;
    else if (!d && this._loading)
      body = html`<div class="state">Loading the quality metrics…</div>`;
    else if (!d) body = html`<div class="state">No metrics for this run.</div>`;
    else
      body = html`
        <div class="tiles">${metricTiles(d).map((t) => this._tile(t))}</div>
        ${this._drift()}
        <div class="notes">
          <p>
            The hours are this run's allocated hours in its own pipeline mode
            (<strong>${d.pipeline_mode}</strong>); the company row of the heatmap, summed over the
            horizon, is the same figure — ${formatHours(d.hours.total)}.
          </p>
          <p>
            The projects are the <strong>demand projects</strong>: every project the run forecast
            except the types left out of demand and the retainers, whose hours are the recurring
            baseline and which have neither assignments nor a schedule by construction.
          </p>
        </div>

        <details
          class="table"
          ?open=${this._tableOpen}
          @toggle=${(e) => (this._tableOpen = e.target.open)}
        >
          <summary>Table view: the metrics and the numbers behind them</summary>
          <data-table
            caption=${`Quality metrics of the run of ${longDate(d.planning_date)}`}
            export-name="quality-metrics"
            page-size="25"
            dense
            .columns=${this._metricColumns}
            .rows=${metricRows(d)}
          ></data-table>
        </details>

        <details
          class="table"
          ?open=${this._taxonomyOpen}
          @toggle=${(e) => (this._taxonomyOpen = e.target.open)}
        >
          <summary>Table view: unmapped source values by dimension</summary>
          <data-table
            caption="Every source system and dimension, with the values live records carry and how many map to nothing"
            export-name="quality-unmapped-values"
            page-size="25"
            dense
            .columns=${this._taxonomyColumns}
            .rows=${taxonomyRows(d)}
          ></data-table>
        </details>
      `;

    return html`
      <div class="card" aria-busy=${this._loading ? 'true' : 'false'}>
        <div class="head">
          <div class="eyebrow">Quality metrics</div>
          <h2>How much of this run is known, and how much did it have to infer?</h2>
          <div class="sub">${this._subtitle()}</div>
        </div>
        ${body}
      </div>
    `;
  }
}

customElements.define('quality-metrics', QualityMetrics);
